import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Reseller } from "../../models/Reseller";
import api from "../../api/axios";

interface ResellersState {
    resellers: Reseller[];
    loading: boolean;
    error: string | null;
  }
  
  const initialState: ResellersState = {
    resellers: [],
    loading: false,
    error: null,
  };
  
  // Thunk to fetch resellers
  export const fetchResellers = createAsyncThunk<Reseller[]>(
    'resellers/fetchResellers',
    async (_, thunkAPI) => {
      try {
        const response = await api.get('/pos/Admin/resellers');
        const list: any[] = response.data || [];

        const toBool = (val: any) => {
          const s = String(val).toLowerCase();
          return s === "true" || s === "t" || s === "1" || val === true;
        };

        const pick = (obj: any, candidates: string[]) => {
          for (const key of candidates) {
            if (obj == null) break;
            const val = obj[key];
            if (val !== undefined && val !== null && String(val).trim() !== "") {
              return val;
            }
          }
          return null;
        };

        const pickFromSources = (sources: any[], candidates: string[]) => {
          for (const source of sources) {
            if (!source) continue;
            const val = pick(source, candidates);
            if (val !== null && val !== undefined) {
              return val;
            }
          }
          return null;
        };

        return list.map((r) => {
          const id =
            pick(r, ["userId", "userid", "id"]) ??
            pick(r, ["resellerId", "resellerid"]);
          const fullName = pick(r, ["name", "fullName", "fullname"]);

          let firstName =
            pick(r, ["firstName", "FirstName", "firstname", "first_name", "first"]) ??
            (fullName ? String(fullName).split(" ")[0] : "");
          let lastName =
            pick(r, ["lastName", "LastName", "lastname", "last_name", "last"]) ??
            (fullName ? String(fullName).split(" ").slice(1).join(" ") : "");

          const displayName =
            pick(r, ["displayName", "DisplayName"]) ??
            fullName ??
            [firstName, lastName].filter(Boolean).join(" ");

          const username =
            pick(r, ["username", "userName", "Username", "user_name"]) ??
            pick(r, ["email", "Email"]) ??
            fullName ??
            "";
          const email = pick(r, ["email", "Email"]) ?? "";

          const balanceRaw =
            pick(r, ["balance", "Balance"]) ??
            pick(r, ["accountBalance", "account_balance"]);

          const resellerIdRaw =
            pick(r, ["resellerId", "resellerid"]) ??
            pick(r, ["id", "userId", "userid"]) ??
            null;
          const resellerId =
            resellerIdRaw != null && resellerIdRaw !== ""
              ? Number(resellerIdRaw)
              : null;

          const userIdRaw = pick(r, ["userId", "userid", "id"]);
          const userId =
            userIdRaw != null && userIdRaw !== ""
              ? Number(userIdRaw)
              : resellerId;

          const profile =
            r?.profile ??
            r?.Profile ??
            r?.resellerProfile ??
            r?.resellerprofile ??
            r?.businessInfo ??
            r?.BusinessInfo ??
            r?.businessinfo ??
            r?.companyInfo ??
            r?.companyinfo ??
            r?.business ??
            r?.Business ??
            null;
          const businessSources = [r, profile];

          const profileEmail =
            pickFromSources([profile, r], [
              "profileEmail",
              "ProfileEmail",
              "profileemail",
              "profile_email",
              "businessEmail",
              "BusinessEmail",
              "business_email",
              "companyEmail",
              "CompanyEmail",
              "company_email",
            ]) ??
            pickFromSources([profile], ["email", "Email"]);

          return {
            userId,
            resellerId,
            firstName: firstName || "",
            lastName: lastName || "",
            username,
            email,
            isAdmin: toBool(pick(r, ["isAdmin", "isadmin", "IsAdmin"])),
            isEsimReseller: toBool(
              pick(r, ["isEsimReseller", "isesimreseller", "IsEsimReseller"])
            ),
            stockAccountId: pick(r, ["stockAccountId", "stockaccountid"]) ?? null,
            salesAccountId: pick(r, ["salesAccountId", "salesaccountid"]) ?? null,
            balance: balanceRaw != null ? Number(balanceRaw) : 0,
            name: fullName ?? "",
            displayName: displayName || username || email || "",
            showPrices: toBool(pick(r, ["showPrices", "showprices"])),
            apiKey: pick(r, ["apiKey", "apikey", "api_key"]),
            balancePassword: pick(r, ["balancePassword", "balancepassword"]),
            businessName: pickFromSources(businessSources, [
              "businessName",
              "BusinessName",
              "businessname",
              "business_name",
            ]),
            registrationNumber: pickFromSources(businessSources, [
              "registrationNumber",
              "RegistrationNumber",
              "registrationnumber",
              "registration_number",
            ]),
            vatNumber: pickFromSources(businessSources, [
              "vatNumber",
              "VatNumber",
              "vatnumber",
              "vat_number",
            ]),
            country: pickFromSources(businessSources, [
              "country",
              "Country",
              "countryName",
              "country_name",
            ]),
            city: pickFromSources(businessSources, ["city", "City"]),
            address: pickFromSources(businessSources, ["address", "Address"]),
            phone: pickFromSources(businessSources, [
              "phone",
              "Phone",
              "phoneNumber",
              "phone_number",
            ]),
            website: pickFromSources(businessSources, [
              "website",
              "Website",
              "webSite",
              "web_site",
              "url",
              "Url",
              "URL",
            ]),
            profileEmail,
          };
        });
      } catch (error: any) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to load resellers');
      }
    }
  );
  
  const resellersSlice = createSlice({
    name: 'resellers',
    initialState,
    reducers: {
      clearResellers(state) {
        state.resellers = [];
        state.loading = false;
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchResellers.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(fetchResellers.fulfilled, (state, action: PayloadAction<Reseller[]>) => {
          state.loading = false;
          state.resellers = action.payload;
        })
        .addCase(fetchResellers.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    },
  });
  
  export const { clearResellers } = resellersSlice.actions;
  export default resellersSlice.reducer;
